import BaseService from './BaseService';
import apiClient from '../plugins/axios';

/**
 * Listede TEL NO / müşteri alanı API'den farklı şekillerde gelebilir (snake_case, camelCase, nested).
 * Tek tip `customer_id` ve `customer_number` alanlarıyla view'a geçirir.
 * @param {Object} row
 * @returns {Object}
 */
function normalizeCallListItem(row) {
  if (row == null || typeof row !== 'object') return row;
  const pickConnId = () => {
    const direct = row.conn_id ?? row.connId ?? row.connection_id ?? row.connectionId ?? row.ConnID;
    if (direct != null && String(direct).trim() !== '') return String(direct).trim();
    const attrs = row.attributes;
    if (attrs && typeof attrs === 'object') {
      const a = attrs.conn_id
        ?? attrs.connId
        ?? attrs.connection_id
        ?? attrs.connectionId;
      if (a != null && String(a).trim() !== '') return String(a).trim();
    }
    return null;
  };
  const pickCustomerId = () => {
    const direct = row.customer_id ?? row.customerId ?? row.CustomerID;
    if (direct != null && String(direct).trim() !== '') return String(direct).trim();
    const cust = row.customer;
    if (cust && typeof cust === 'object') {
      const n = cust.id ?? cust.customer_id ?? cust.customerId;
      if (n != null && String(n).trim() !== '') return String(n).trim();
    }
    const attrs = row.attributes;
    if (attrs && typeof attrs === 'object') {
      const a = attrs.customer_id ?? attrs.customerId;
      if (a != null && String(a).trim() !== '') return String(a).trim();
    }
    return null;
  };
  const pickCustomerNumber = () => {
    const direct = row.customer_number ?? row.customerNumber ?? row.CustomerNumber;
    if (direct != null && String(direct).trim() !== '') return String(direct).trim();
    const cust = row.customer;
    if (cust && typeof cust === 'object') {
      const n = cust.number ?? cust.customer_number ?? cust.customerNumber;
      if (n != null && String(n).trim() !== '') return String(n).trim();
    }
    const attrs = row.attributes;
    if (attrs && typeof attrs === 'object') {
      const a = attrs.customer_number ?? attrs.customerNumber;
      if (a != null && String(a).trim() !== '') return String(a).trim();
    }
    return null;
  };
  const connId = pickConnId();
  const customerId = pickCustomerId();
  const customerNumber = pickCustomerNumber();
  const patch = {};
  if (connId != null && row.conn_id !== connId) {
    patch.conn_id = connId;
  }
  if (customerId != null && row.customer_id !== customerId) {
    patch.customer_id = customerId;
  }
  if (customerNumber != null && row.customer_number !== customerNumber) {
    patch.customer_number = customerNumber;
  }
  if (Object.keys(patch).length === 0) return row;
  return { ...row, ...patch };
}

function normalizeCallListPagination(raw, requestedPage, requestedPageSize, itemCount) {
  const pageSize = requestedPageSize > 0 ? requestedPageSize : 20;
  const page = Number.isFinite(Number(requestedPage)) && Number(requestedPage) > 0
    ? Number(requestedPage)
    : 1;

  if (raw != null && typeof raw === 'object') {
    const p = Number(raw.page ?? raw.Page);
    const ps = Number(raw.page_size ?? raw.pageSize ?? raw.PageSize);
    const pageNum = Number.isFinite(p) && p > 0 ? p : page;
    const pageSizeNum = Number.isFinite(ps) && ps > 0 ? ps : pageSize;
    let total = null;
    if (raw.total != null) total = Number(raw.total);
    else if (raw.Total != null) total = Number(raw.Total);
    if (total != null && !Number.isFinite(total)) total = null;
    let totalPages = raw.total_pages ?? raw.totalPages ?? raw.TotalPages;
    if (totalPages != null) totalPages = Number(totalPages);
    if ((!Number.isFinite(totalPages) || totalPages < 1) && total != null && Number.isFinite(total) && pageSizeNum > 0) {
      totalPages = Math.max(1, Math.ceil(total / pageSizeNum));
    }
    const totalPagesVal = Number.isFinite(totalPages) ? totalPages : null;
    const out = {
      page: pageNum,
      page_size: pageSizeNum,
      total: Number.isFinite(total) ? total : null,
      total_pages: totalPagesVal,
      inferredMore: false,
    };
    if (totalPagesVal == null && itemCount >= pageSizeNum) {
      out.inferredMore = true;
    }
    return out;
  }

  const fullPage = itemCount >= pageSize;
  return {
    page,
    page_size: pageSize,
    total: fullPage ? null : itemCount,
    total_pages: fullPage ? null : page,
    inferredMore: fullPage,
  };
}

function getHeaderCaseInsensitive(headers, headerName) {
  if (!headers || typeof headers !== 'object') return '';
  const target = String(headerName || '').toLowerCase();
  const keys = Object.keys(headers);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (String(k).toLowerCase() === target) {
      const v = headers[k];
      if (Array.isArray(v)) return v.join(', ');
      return v == null ? '' : String(v);
    }
  }
  return '';
}

function parseFilenameFromContentDisposition(disposition) {
  const d = String(disposition || '').trim();
  if (!d) return null;

  const star = d.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star?.[1]) {
    const raw = star[1].trim().replace(/^"+|"+$/g, '');
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  }

  const quoted = d.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1].trim();

  const bare = d.match(/filename=([^;\s]+)/i);
  if (bare?.[1]) return bare[1].trim().replace(/^"+|"+$/g, '');

  return null;
}

/**
 * Call History Service - /calls API
 * List calls and get call details (transcript, sentiment). Full-call audio: GET .../audio.
 */
class CallHistoryService extends BaseService {
  constructor() {
    super('calls');
  }

  buildListQuery(params = {}) {
    const query = {
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    };
    const callId = params.call_id != null && String(params.call_id).trim() !== ''
      ? String(params.call_id).trim()
      : null;
    if (callId) {
      query.call_id = callId;
    }
    const connId = params.conn_id != null && String(params.conn_id).trim() !== ''
      ? String(params.conn_id).trim()
      : null;
    if (connId) {
      query.conn_id = connId;
    }
    const agentId = params.agent_id != null && String(params.agent_id).trim() !== ''
      ? String(params.agent_id).trim()
      : null;
    if (agentId) {
      query.agent_id = agentId;
    }
    const customerId = params.customer_id != null && String(params.customer_id).trim() !== ''
      ? String(params.customer_id).trim()
      : null;
    if (customerId) {
      query.customer_id = customerId;
    }
    const customerNumber = params.customer_number != null && String(params.customer_number).trim() !== ''
      ? String(params.customer_number).trim()
      : null;
    if (customerNumber) {
      query.customer_number = customerNumber;
    }
    const startDate = Number(params.start_date);
    if (Number.isFinite(startDate) && startDate > 0) {
      query.start_date = Math.floor(startDate);
    }
    const endDate = Number(params.end_date);
    if (Number.isFinite(endDate) && endDate > 0) {
      query.end_date = Math.floor(endDate);
    }
    return query;
  }

  /**
   * GET /calls/export — filtered calls as CSV file.
   * @param {Object} params - Same filter params with list endpoint.
   * @returns {Promise<{ blob: Blob, filename: string|null }>}
   */
  async exportCalls(params = {}) {
    const query = this.buildListQuery(params);
    delete query.page;
    delete query.page_size;

    const response = await apiClient.get(`${this.resource}/export`, {
      params: query,
      responseType: 'blob',
    });

    const disposition = getHeaderCaseInsensitive(response.headers, 'content-disposition');
    const filename = parseFilenameFromContentDisposition(disposition);

    return { blob: response.data, filename };
  }

  /**
   * GET /calls/{id}/audio — tam görüşme sesi (Bearer ile blob; HTML audio için).
   */
  async fetchAudioBlob(callId) {
    const response = await apiClient.get(`calls/${callId}/audio`, {
      responseType: 'blob',
      skipLoading: true,
      skipErrorNotification: true,
    });
    return response.data;
  }

  /**
   * List calls with optional search
   * @param {Object} params - { search?: string }
   * @returns {Promise<{ success: boolean, data: Array }>}
   */
  async getList(params = {}) {
    try {
      const response = await this.getAll(params);
      const data = Array.isArray(response) ? response : (response?.data ?? []);
      return { success: true, data };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('CallHistoryService.getList:', error);
      return { success: false, data: [] };
    }
  }

  /**
   * GET /calls — paginated list (page, page_size; optional call_id, conn_id, agent_id, customer_id, customer_number, start_date, end_date).
   * API envelope (axios response.data), örnek:
   * { data: ListCallResponse[], pagination: { page, page_size, total, total_pages }, success, status_code }
   * @param {Object} params - { page?: number, page_size?: number, call_id?: string, conn_id?: string, agent_id?: string, customer_id?: string, customer_number?: string, start_date?: number, end_date?: number }
   * @returns {Promise<{ success: boolean, items: Array, pagination: Object|null }>}
   */
  async getListPage(params = {}) {
    const query = this.buildListQuery(params);
    try {
      const response = await apiClient.get(this.resource, {
        params: query,
        skipLoading: true,
      });
      const body = response.data || {};
      let paginationRaw = body.pagination ?? body.Pagination;

      let items = [];
      const dataField = body.data ?? body.Data;
      if (Array.isArray(dataField)) {
        items = dataField;
      } else if (dataField && typeof dataField === 'object') {
        if (paginationRaw == null && dataField.pagination != null) {
          paginationRaw = dataField.pagination;
        }
        if (Array.isArray(dataField.items)) {
          items = dataField.items;
        } else if (Array.isArray(dataField.records)) {
          items = dataField.records;
        } else if (Array.isArray(dataField.calls)) {
          items = dataField.calls;
        }
      }

      items = items.map(normalizeCallListItem);

      const pagination = normalizeCallListPagination(
        paginationRaw,
        query.page,
        query.page_size,
        items.length,
      );

      return { success: body.success !== false, items, pagination };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('CallHistoryService.getListPage:', error);
      return { success: false, items: [], pagination: null };
    }
  }

  /**
   * Get call details (transcript, sentiment, etc.)
   * @param {string} id - Call ID
   * @returns {Promise<{ success: boolean, data: Object|null }>}
   */
  async getCallDetails(id) {
    try {
      const data = await this.getById(id);
      return { success: true, data: data || null };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('CallHistoryService.getCallDetails:', error);
      return { success: false, data: null };
    }
  }
}

export default new CallHistoryService();
